/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect, useImperativeHandle, useMemo, useState} from "react"
import {useTranslation} from "react-i18next"
import moment from "moment"
import cx from "classnames"
import {AdvisorsPopup, AdvisorTitle, KlassappTable, KlassappTableHeader} from "uiKit"
import {KlassappTableHOC} from "HOC"
import {
    autoDialerService,
    coreSocket,
    fieldsService,
    reportsService,
    studentService,
    studentServiceV2,
    taskService,
    userProfileServiceV3
} from "services"
import {
    checkHasPermissionToUpdateAdvisor,
    getFullName,
    handleError,
    renderList,
    toDateOnly,
    toastInfo,
    toastSuccess
} from "helpers"
import {Auth} from "types/auth"
import {BaseDepartmentId, DepartmentDialerModuleMapping} from "types/departments"
import styles from "./StudentListTable.module.css"
import {useAllLeadSources, useAllLeadTypes, useAllVerificationStatuses, useModel, useVisible} from "hooks"
import {Icon} from "components/Icon"
import {ActivityPopup} from "sections/shared/student"
import {cloneDeep, get, head, isEmpty, map, uniq, uniqBy} from "lodash"
import {DUE_COLOR_OPTIONS} from "types/contact-strategy"
import {KlassDropdown} from "components"
import {KlassappTableProps} from "types/common"
import {FieldOperator, FilterKey, FilterList, formatDateFilterValue, resolveYesNoFilterValue} from "types/filter"
import {useEdularModulesContext} from "@edular/modules"
import {SOCKET_ROOM_PRIORITY_TABLE, SocketEvent} from "helpers/socket"
import {offEvent, onEvent} from "helpers/eventHelper"
import {
    EVENT_STUDENT_ACTIVITY_COMPLETION_TOGGLED,
    EVENT_STUDENT_ACTIVITY_CREATED,
    EVENT_STUDENT_ACTIVITY_READ,
    EVENT_STUDENT_ACTIVITY_UNREAD,
    StudentActivity
} from "types/activity"
import {StudentStatusLabel} from "components/StudentStatusLabel"
import {StudentStatus} from "types/students"
import {exportCsv, exportExcel} from "helpers/export-table"
import {ExportFileType} from "components/ui"
import {ChatModalV2} from "sections/NewCommunication/shared"
import {useAllAvailableStudentStatuses} from "hooks/student-statuses/useAllAvailableStudentStatuses"
import {FinAidOfferStatus} from "types/fin-aid/fin-aid"
import {ChangeStatusPopup, PopupSpecialEvent} from "./parts"
import {RetentionCategory} from "types/student-services/sap"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import StudentTableIconWithIndicator from "components/StudentTableIconWithIndicator"
import {TaskSubtype} from "types/tasks"
import {TOTAL_HOURS_COMPLETED_REPORT_FORM_CODE} from "types/attendance"
import {useTasks} from "sections/Tasks/hooks/useTasks"
import {PopupCreateTask} from "sections/shared/student/PopupCreateTask/PopupCreateTask"
import {Settings} from "types/settings"
import {useQuery} from "@tanstack/react-query"
const MODULE_REPORT_INFO = {
    [BaseDepartmentId.Admissions]: {
        reportKey: "admissions/students",
        filename: ["admissions-student-report", moment().format("DD_MM_YYYY-hhmm")].join("-")
    },
    [BaseDepartmentId.FinancialAid]: {
        reportKey: "financialAid/students",
        filename: ["financial-aid-student-report", moment().format("DD_MM_YYYY-hhmm")].join("-")
    }
}

const MODULE_REPORT_INFO_NEW_FILTERS = {
    [BaseDepartmentId.Admissions]: {
        reportKey: "newFilter_admissions/students",
        filename: ["admissions-student-report", moment().format("DD_MM_YYYY-hhmm")].join("-")
    },
    [BaseDepartmentId.FinancialAid]: {
        reportKey: "newFilter_financialAid/students",
        filename: ["financial-aid-student-report", moment().format("DD_MM_YYYY-hhmm")].join("-")
    },
    [BaseDepartmentId.Academics]: {
        reportKey: "newFilter_academics/students",
        filename: ["academics-student-report", moment().format("DD_MM_YYYY-hhmm")].join("-")
    }
}

type Props = KlassappTableProps & {
    departmentId: number
    studentForm: any
    filterMemoryKey: FilterKey
    useNewFilter: boolean
    exportFileType: ExportFileType
    studentFormSearch?: string
    canShowAllStudents?: boolean
    canHaveAccessOthers?: boolean
    studentId?: number
    studentStatusChange?: any
    enrollmentProfileIdChange?: any
    advisorTitle?: AdvisorTitle
    isFinancialVerificationTab?: boolean
    isFinancialPackagingTab?: boolean
    updateState: (data: any) => void
    reloadStudentAdvisors?: () => void
    showOnlyHighschool?: boolean
    showOnlyPartner?: boolean
    innerRef?: React.Ref<StudentListTableRef>
}

export interface StudentListTableRef {
    loadAndHighlightStudent: (profileId: number) => Promise<void>
}

const StudentListTable = React.forwardRef<StudentListTableRef, Props>((props, forwardedRef) => {
    const ref = props.innerRef || forwardedRef
    const model = useModel()
    const {
        data,
        page,
        total,
        pageSize,
        columns,
        fields,
        allFields,
        isLoading,
        orderField,
        getCurrentPage,
        getCurrentPageSize,
        getCurrentData,
        studentForm,
        studentFormSearch,
        canShowAllStudents,
        canHaveAccessOthers,
        departmentId,
        studentId,
        isShowTableHeaderAction,
        tableHeaderActions,
        studentStatusChange,
        enrollmentProfileIdChange,
        isLoadedTableFuncs,
        filterMemoryKey,
        reloadStudentAdvisors,
        exportFileType,
        isFinancialPackagingTab = false,
        isFinancialVerificationTab = false,
        useNewFilter = false,
        showOnlyHighschool = false,
        showOnlyPartner = false
    } = props
    const {t} = useTranslation(["common", "financialAid"])
    const userDateTimeFormat = model.getUserDateTimeFormat()
    const userDateFormat = model.getUserDateFormat()
    const {statuses: studentStatuses} = useAllAvailableStudentStatuses()
    const [studentProfileIds, setStudentProfileIds] = useState<number[]>([])
    const [studentCampusIds, setStudentCampusIds] = useState<number[]>([])
    const [type, setType] = useState<string>("")
    const advisorsPopup = useVisible(false)
    const taskPopup = useVisible(false)
    const hasPermissionToUpdateAdvisor = checkHasPermissionToUpdateAdvisor(departmentId, model) || model.isAdmin()
    const chatPopup = useVisible(false)
    const activityPopup = useVisible(false)
    const changeStatusPopup = useVisible(false)
    const specialEventPopup = useVisible(false)
    const [selectedStudent, setSelectedStudent] = React.useState<Auth.DepartmentStudent | undefined>()
    const loadingSeqRef = React.useRef(Math.random())
    const activeStudentRef = React.useRef<number | undefined>()
    const {isModuleEnable} = useEdularModulesContext()
    const {leadSources} = useAllLeadSources()
    const {leadTypes} = useAllLeadTypes()
    const {verificationStatuses} = useAllVerificationStatuses()
    const [isFirstLoaded, setFirstLoaded] = useState(false)
    const [statusStudents, setStatusStudents] = useState([])
    const {DEFAULT_TASK} = useTasks()
    const faTab = isFinancialVerificationTab ? "verification" : isFinancialPackagingTab ? "packaging" : undefined
    const [taskData, setTaskData] = useState<any>(cloneDeep(DEFAULT_TASK))

    const [hightlightedStudentId, setHighlightedStudentId] = useState<number | null>(null)
    const customFieldModuleByDepartment = {
        [BaseDepartmentId.Admissions]: Settings.CustomFieldModule.Admissions,
        [BaseDepartmentId.FinancialAid]: Settings.CustomFieldModule.FinancialAid,
        [BaseDepartmentId.Academics]: Settings.CustomFieldModule.Academics,
        [BaseDepartmentId.StudentServices]: Settings.CustomFieldModule.StudentServices,
        [BaseDepartmentId.CareerServices]: Settings.CustomFieldModule.CareerServices,
        [BaseDepartmentId.StudentAccount]: Settings.CustomFieldModule.StudentAccount
    }
    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
        loadAndHighlightStudent: async (profileId: number) => {
            setHighlightedStudentId(profileId)
            await loadStudent(profileId, true)
            onChangeActiveStudent(profileId)
        }
    }))

    const customFieldModule = customFieldModuleByDepartment[props.departmentId]

    const {data: customFields, isSuccess} = useQuery(
        ["module-active-custom-fields", customFieldModule],
        () =>
            fieldsService.getCustomFields({
                filter: {modules: [customFieldModule], isActive: true},
                range: {page: 1, pageSize: 1000}
            }),
        {onError: handleError, enabled: !!customFieldModule}
    )

    useEffect(() => {
        getTask()
    }, [])

    useEffect(() => {
        if (studentId) {
            onChangeActiveStudent(studentId)
        }
    }, [studentId])

    useEffect(() => {
        if (!studentStatusChange) return
        const newStatus = studentStatuses.find((status) => status?.statusId === studentStatusChange?.statusId)
        if (!newStatus) return
        props.dispatch({
            data: getCurrentData().map((student) =>
                student.profileId !== studentStatusChange.profileId
                    ? student
                    : {
                          ...student,
                          profileState: newStatus.state,
                          studentStatusHtml: renderStudentStudentStatus(newStatus)
                      }
            )
        })
    }, [studentStatusChange, studentStatuses])

    useEffect(() => {
        if (!enrollmentProfileIdChange) return
        ;(async function loadNewStudentEnrollment() {
            try {
                const {
                    data: [newStudent]
                } = await studentService.getDepartmentStudents({
                    skipTotal: true,
                    filter: {
                        departmentId,
                        profileIds: [enrollmentProfileIdChange.newProfileId],
                        faTab
                    }
                })
                if (!newStudent) return
                props.dispatch({
                    data: getCurrentData().map((student, index) =>
                        student.profileId !== enrollmentProfileIdChange.profileId
                            ? student
                            : getStudentRecord(newStudent, index)
                    )
                })
            } catch (error) {
                handleError(error)
            }
        })()
    }, [enrollmentProfileIdChange, faTab])

    useEffect(() => {
        if (exportFileType) {
            handleExportFileTypeSelect(exportFileType)
        }
    }, [exportFileType])

    const getFilter = () => {
        const filters = {...studentForm, ...model.filterUserList}
        const {
            campus,
            program,
            advisor,
            acadAdvisors,
            faAdvisors,
            admAdvisors,
            term,
            startingTerm,
            checklist,
            status,
            state,
            profileStatus,
            applicationCompletedDate,
            startDateRange,
            createdAtRange,
            inquiryDateRange,
            lastActivityDateRange,
            includeArchive = false,
            showDueOnly = false,
            fullyEnrolled,
            onlySEV,
            specialEvent,
            sapCheckpoints,
            retentionLevels,
            studentStatusIds,
            admissionsLeadSourceIds,
            leadTypeIds,
            activityIds,
            activityDescription,
            faAwardYear,
            faTerm
        } = filters
        let filter: any = {
            departmentId: props.departmentId,
            includeArchive,
            showDueOnly,
            onlySEV,
            activityDescription,
            faTab,
            faAwardYear,
            faTermId: faTerm?.id
        }
        if (fullyEnrolled) {
            filter.fullyEnrolled = true
        }
        if (specialEvent?.value != null) {
            filter.specialEvent = resolveYesNoFilterValue(specialEvent.value)
        }
        if (campus) {
            filter.campusIds = campus.map(({id}) => id)
        }
        if (program) {
            filter.programIds = program.map(({id}) => id)
        }
        if (studentStatusIds) {
            filter.studentStatusIds = studentStatusIds.map(({statusId}) => statusId)
        }
        if (admissionsLeadSourceIds) {
            filter.admissionsLeadSourceIds = admissionsLeadSourceIds.map(({id}) => id)
        }
        if (leadTypeIds) {
            filter.leadTypeIds = leadTypeIds
        }

        if (canShowAllStudents || canHaveAccessOthers) {
            if (isEmpty(advisor)) {
                filter.advisorProfileIds = []
            } else {
                filter.advisorProfileIds = (advisor || []).map((item) => item?.profileId).filter((item) => item)
            }
        } else {
            const profile = model.user.profiles.find((profile) => profile.type === Auth.UserProfileType.Staff)
            filter.advisorProfileIds = [profile.id]
        }

        if (admAdvisors?.length) {
            filter.admAdvisorProfileIds = admAdvisors.map((profile) => profile.profileId)
        }
        if (faAdvisors?.length) {
            filter.faAdvisorProfileIds = faAdvisors.map((profile) => profile.profileId)
        }
        if (acadAdvisors?.length) {
            filter.acadAdvisorProfileIds = acadAdvisors.map((profile) => profile.profileId)
        }

        filter.checklistCompleted = checklist === "completed"
        if (status?.length) {
            filter.statusIds = status.map(({statusId}) => statusId)
        }
        if (state) {
            filter.states = state.map(({id}) => id)
        }
        if (profileStatus) {
            filter.profileStatus = profileStatus.map(({id}) => id === "active")
        }
        if (term) {
            filter.termsIds = term.map(({id}) => id)
        }
        if (startingTerm) {
            filter.startingTermIds = startingTerm.map(({id}) => id)
        }
        if (activityIds) {
            filter.activityIds = activityIds.map(({activityId}) => activityId)
        }
        if (applicationCompletedDate) {
            filter.applicationCompletedDate = moment(applicationCompletedDate).format("YYYY-MM-DD")
        }
        if (startDateRange && startDateRange.length) {
            filter.startDateRange = [
                moment(startDateRange[0]).format("YYYY-MM-DD"),
                moment(startDateRange[1]).format("YYYY-MM-DD")
            ]
        }
        if (createdAtRange && createdAtRange.length) {
            filter.createdAtRange = [
                moment(createdAtRange[0]).startOf("date").toISOString(),
                moment(createdAtRange[1]).endOf("date").toISOString()
            ]
        }
        if (inquiryDateRange && inquiryDateRange.length) {
            filter.inquiryDateRange = [
                moment(inquiryDateRange[0]).format("YYYY-MM-DD"),
                moment(inquiryDateRange[1]).format("YYYY-MM-DD")
            ]
        }
        if (lastActivityDateRange && lastActivityDateRange.length) {
            filter.lastActivityDateRange = [
                moment(lastActivityDateRange[0]).startOf("date").toISOString(),
                moment(lastActivityDateRange[1]).endOf("date").toISOString()
            ]
        }
        if (studentFormSearch) {
            filter.search = studentFormSearch
        }
        if (sapCheckpoints) {
            filter.sapCheckpoints = sapCheckpoints.map(({id}) => id)
        }
        if (retentionLevels) {
            filter.retentionLevels = retentionLevels.map(({id}) => id)
        }

        return filter
    }

    const getNewFilter = useCallback(() => {
        const filters = {...studentForm}
        const {
            profiles,
            campus,
            program,
            acadAdvisors,
            faAdvisors,
            admAdvisors,
            term,
            startingTerm,
            checklist,
            status,
            state,
            profileStatus,
            applicationCompletedDate,
            startDateRange,
            createdAtRange,
            inquiryDateRange,
            lastActivityDateRange,
            includeArchive,
            showDueOnly,
            fullyEnrolled,
            onlySEV,
            specialEvent,
            sapCheckpoints,
            retentionLevels,
            studentStatusIds,
            oldStudentStatusIds,
            oldStudentStatusDateRange,
            admissionsLeadSourceIds,
            leadTypeIds,
            isAccessDisabled,
            phases,
            shifts,
            activityIds,
            activityDescription,
            faAwardYear,
            faTerm,
            temporaryOutDateRange,
            withdrawalDateRange,
            users,
            countries,
            programVersions,
            showAcceptedAdmission,
            showAccommodatedHours,
            admissionAcceptanceDateRange,
            fullyEnrolledDateRange,
            groups,
            highschool,
            partner,
            campaigns
        } = filters

        let filter: FilterList = {}
        if (fullyEnrolled) filter.fullyEnrolled = fullyEnrolled
        if (includeArchive) filter.includeArchive = includeArchive
        if (showDueOnly) filter.showDueOnly = showDueOnly
        if (onlySEV) filter.onlySEV = onlySEV
        if (specialEvent) {
            filter.specialEvent = {
                ...specialEvent,
                value: resolveYesNoFilterValue(specialEvent.value)
            }
        }
        if (showAcceptedAdmission) filter.showAcceptedAdmission = showAcceptedAdmission
        if (showAccommodatedHours) filter.showAccommodatedHours = showAccommodatedHours
        if (checklist) {
            filter.checklistCompleted = {
                operator: checklist.operator,
                value: checklist.value.id
            }
        }
        if (faTab) {
            filter.faTab = {
                operator: FieldOperator.Equal,
                value: faTab
            }
        }
        if (faAwardYear) {
            filter.faAwardYear = {
                operator: FieldOperator.Equal,
                value: faAwardYear
            }
        }
        if (faTerm) {
            filter.faTermId = {
                operator: FieldOperator.Equal,
                value: faTerm.id
            }
        }

        if (profiles) {
            filter.profileIds = {
                operator: profiles.operator,
                value: profiles.value.map(({profileId}) => profileId)
            }
        }
        if (campus) {
            filter.campusIds = {
                operator: campus.operator,
                value: campus.value.map(({id}) => id)
            }
        }
        if (program) {
            filter.programIds = {
                operator: program.operator,
                value: program.value.map(({id}) => id)
            }
        }
        if (studentStatusIds) {
            filter.studentStatusIds = {
                operator: studentStatusIds.operator,
                value: studentStatusIds.value.map(({statusId}) => statusId)
            }
        }
        if (oldStudentStatusIds) {
            filter.oldStudentStatusIds = {
                operator: oldStudentStatusIds.operator,
                value: oldStudentStatusIds.value.map(({statusId}) => statusId)
            }
        }
        if (oldStudentStatusDateRange) {
            filter.oldStudentStatusDateRange = {
                operator: oldStudentStatusDateRange.operator,
                value: formatDateFilterValue(oldStudentStatusDateRange.operator, oldStudentStatusDateRange.value)
            }
        }
        if (admissionsLeadSourceIds) {
            filter.admissionsLeadSourceIds = {
                operator: admissionsLeadSourceIds.operator,
                value: admissionsLeadSourceIds.value.map(({id}) => id)
            }
        }
        if (leadTypeIds) {
            filter.leadTypeIds = {
                operator: leadTypeIds.operator,
                value: leadTypeIds.value.map(({leadTypeId}) => leadTypeId)
            }
        }
        if (isAccessDisabled) {
            filter.isAccessDisabled = {
                ...isAccessDisabled,
                value: !!isAccessDisabled?.value?.id
            }
        }
        if (phases) {
            filter.phases = {
                ...phases,
                value: phases.value.map(({phaseId}) => phaseId)
            }
        }
        if (shifts) {
            filter.shifts = {
                ...shifts,
                value: shifts.value.map(({shiftId}) => shiftId)
            }
        }

        // if (!canShowAllStudents && !canHaveAccessOthers) {
        //     const profile = model.user.profiles.find((profile) => profile.type === Auth.UserProfileType.Staff)
        //     filter.advisorProfileIds = {
        //         operator: FieldOperator.Equal,
        //         value: [profile.id]
        //     }
        // }

        if (admAdvisors) {
            filter.admAdvisorProfileIds = {
                operator: admAdvisors.operator,
                value: admAdvisors.value.map((profile) => profile.profileId)
            }
        }
        if (faAdvisors) {
            filter.faAdvisorProfileIds = {
                operator: faAdvisors.operator,
                value: faAdvisors.value.map((profile) => profile.profileId)
            }
        }
        if (acadAdvisors) {
            filter.acadAdvisorProfileIds = {
                operator: acadAdvisors.operator,
                value: acadAdvisors.value.map((profile) => profile.profileId)
            }
        }

        if (status) {
            filter.statusIds = {
                operator: status.operator,
                value: status.value.map(({statusId}) => statusId)
            }
        }
        if (state) {
            filter.states = {
                operator: state.operator,
                value: state.value.map(({id}) => id)
            }
        }
        if (profileStatus) {
            filter.profileStatuses = {
                operator: profileStatus.operator,
                value: profileStatus.value.map(({id}) => id === "active")
            }
        }
        if (term) {
            filter.termIds = {
                operator: term.operator,
                value: term.value.map(({id}) => id)
            }
        }
        if (startingTerm) {
            filter.startingTermIds = {
                operator: startingTerm.operator,
                value: startingTerm.value.map(({id}) => id)
            }
        }
        if (activityIds) {
            filter.activityIds = {
                operator: activityIds.operator,
                value: activityIds.value.map(({activityId}) => activityId)
            }
        }
        if (activityDescription) {
            filter.activityDescription = activityDescription
        }
        if (applicationCompletedDate) {
            filter.applicationCompletedDate = {
                operator: applicationCompletedDate.operator,
                value: formatDateFilterValue(applicationCompletedDate.operator, applicationCompletedDate.value)
            }
        }
        if (startDateRange) {
            filter.startDateRange = {
                operator: startDateRange.operator,
                value: formatDateFilterValue(startDateRange.operator, startDateRange.value)
            }
        }
        if (createdAtRange) {
            filter.createdAtRange = {
                operator: createdAtRange.operator,
                value: formatDateFilterValue(createdAtRange.operator, createdAtRange.value)
            }
        }
        if (inquiryDateRange) {
            filter.inquiryDateRange = {
                operator: inquiryDateRange.operator,
                value: formatDateFilterValue(inquiryDateRange.operator, inquiryDateRange.value)
            }
        }
        if (lastActivityDateRange) {
            filter.lastActivityDateRange = {
                operator: lastActivityDateRange.operator,
                value: formatDateFilterValue(lastActivityDateRange.operator, lastActivityDateRange.value)
            }
        }
        if (sapCheckpoints) {
            filter.sapCheckpoints = {
                operator: sapCheckpoints.operator,
                value: sapCheckpoints.value.map(({id}) => id)
            }
        }
        if (retentionLevels) {
            filter.retentionLevels = {
                operator: retentionLevels.operator,
                value: retentionLevels.value.map(({id}) => id)
            }
        }

        Object.values(RetentionCategory).forEach((x) => {
            const keyName = `riskLevel${x}`
            if (filters[keyName]) {
                filter[keyName] = {
                    operator: filters[keyName].operator,
                    value: filters[keyName].value?.id
                }
            }
        })

        if (temporaryOutDateRange) {
            filter.temporaryOutDateRange = {
                operator: temporaryOutDateRange.operator,
                value: formatDateFilterValue(temporaryOutDateRange.operator, temporaryOutDateRange.value)
            }
        }
        if (withdrawalDateRange) {
            filter.withdrawalDateRange = {
                operator: withdrawalDateRange.operator,
                value: formatDateFilterValue(withdrawalDateRange.operator, withdrawalDateRange.value)
            }
        }

        if (users) {
            filter.userIds = {
                operator: users.operator,
                value: users.value.map(({id}) => id)
            }
        }

        if (countries) {
            filter.countryIds = {
                operator: countries.operator,
                value: countries.value.map(({name}) => name)
            }
        }

        if (programVersions) {
            filter.programVersionIds = {
                operator: programVersions.operator,
                value: programVersions.value.map(({id}) => id)
            }
        }

        if (admissionAcceptanceDateRange) {
            filter.admissionAcceptanceDateRange = {
                operator: admissionAcceptanceDateRange.operator,
                value: formatDateFilterValue(admissionAcceptanceDateRange.operator, admissionAcceptanceDateRange.value)
            }
        }

        if (fullyEnrolledDateRange) {
            filter.fullyEnrolledDateRange = {
                operator: fullyEnrolledDateRange.operator,
                value: formatDateFilterValue(fullyEnrolledDateRange.operator, fullyEnrolledDateRange.value)
            }
        }

        if (groups) {
            filter.groupIds = {
                operator: groups.operator,
                value: groups.value.map(({id}) => id)
            }
        }

        if (showOnlyHighschool) {
            filter.onlyHighschool = {
                operator: FieldOperator.Equal,
                value: true
            }
        }
        if (showOnlyPartner) {
            filter.onlyPartner = {
                operator: FieldOperator.Equal,
                value: true
            }
        }
        if (highschool) {
            filter.highschoolIds = {
                operator: highschool.operator,
                value: highschool.value.map(({id}) => id)
            }
        }
        if (partner) {
            filter.partnerIds = {
                operator: partner.operator,
                value: partner.value.map(({id}) => id)
            }
        }
        if (campaigns) {
            filter.campaignIds = {
                operator: campaigns.operator,
                value: campaigns.value.map(({id}) => id)
            }
        }

        return {
            filters: filter,
            departmentId: props.departmentId,
            search: studentFormSearch
        }
    }, [props.departmentId, studentFormSearch, studentForm, canShowAllStudents, canHaveAccessOthers])

    const renderListHtml = React.useCallback((list: any[], field?: string) => {
        return <span>{list?.map((item) => (field ? item[field] : item))?.join(", ")}</span>
    }, [])

    const renderStudentStudentStatus = (status: StudentStatus) => {
        return <StudentStatusLabel status={status} />
    }

    const renderChecklistStatusHtml = React.useCallback(
        (status: Auth.CheckListStatus, stats?: Auth.StudentChecklistStats) => {
            return (
                <div className={styles.statusContainer}>
                    <span className={cx(styles.status, styles[`status-${status}`])}>{`${status} ${
                        stats ? `(${stats.completed}/${stats.total})` : ""
                    }`}</span>
                </div>
            )
        },
        []
    )

    const updateProfileAdmissionLeadSource = React.useCallback(
        async (profileId: number, admissionsLeadSourceId: number) => {
            try {
                await userProfileServiceV3.update({id: profileId, admissionsLeadSourceId})
            } catch (error) {
                handleError(error)
            }
        },
        []
    )

    const updateProfileLeadType = React.useCallback(async (profileId: number, leadTypeId: number) => {
        try {
            await userProfileServiceV3.update({id: profileId, leadTypeId})
        } catch (error) {
            handleError(error)
        }
    }, [])

    const renderApplicationFeeHtml = React.useCallback((applicationFee: string) => {
        switch (applicationFee) {
            case Auth.ApplicationFee.Pay:
                return "Pay"
            case Auth.ApplicationFee.NotPay:
                return "Not Pay"
            case Auth.ApplicationFee.Wait:
                return "Wait"
            default:
                return ""
        }
    }, [])

    const renderTickHtml = React.useCallback((record: Auth.DepartmentStudent) => {
        return (
            <StudentTableIconWithIndicator
                icon="CHECKMARK_CIRCLE"
                onClick={() => {
                    setSelectedStudent(record)
                    activityPopup.open()
                }}
                showIndicator={!!record.unreadActivityIds?.length}
            />
        )
    }, [])

    const renderChatHtml = React.useCallback((record) => {
        return (
            <StudentTableIconWithIndicator
                icon="MESSAGE"
                onClick={() => {
                    setSelectedStudent(record)
                    chatPopup.open()
                }}
                showIndicator={false}
            />
        )
    }, [])

    const getStudentRecord = React.useCallback(
        (student: Auth.DepartmentStudent, index: number): Auth.DepartmentStudent & Record<string, any> => {
            const departmentDue = student.departmentDues?.find((due) => due.departmentId === departmentId)
            return {
                ...student,
                id: `${student.profileId}_${index}`,
                firstName: student.firstName || "",
                name: getFullName(student),
                createdAt: moment(student.createdAt).format(userDateTimeFormat),
                lastActivityDate: student.latestActivity
                    ? moment(student.latestActivity.updatedAt).format(userDateTimeFormat)
                    : "",
                lastActivity: student.latestActivity?.name,
                admissionAdvisorsHtml: renderListHtml(student.admissionAdvisors, "fullName"),
                finAidAdvisorsHtml: renderListHtml(student.finAidAdvisors, "fullName"),
                campusHtml: renderListHtml(student.campuses),
                admChecklistStatusHtml: renderChecklistStatusHtml(
                    student.admChecklistStatus,
                    student.admChecklistStats
                ),
                faChecklistStatusHtml: renderChecklistStatusHtml(student.faChecklistStatus, student.faChecklistStats),
                applicationFeeHtml: renderApplicationFeeHtml(student.applicationFee),
                preferredTimeOfContact: student.preferredTimeOfContact,
                rowStyle: departmentDue ? {background: departmentDue.dueColor || DUE_COLOR_OPTIONS[0].id} : undefined,
                isActiveTableCol: student.profileId === activeStudentRef.current,
                studentStatusHtml: student.status ? renderStudentStudentStatus(student.status) : null
            }
        },
        [departmentId, renderListHtml, renderStudentStudentStatus, renderChecklistStatusHtml, renderApplicationFeeHtml]
    )

    const getStudentRecordByReport = (student: Auth.DepartmentStudent) => {
        return {
            ...student,
            id: student.profileId,
            firstName: student.firstName || "",
            name: getFullName(student),
            createdAt: moment.utc(student.createdAt).format(userDateTimeFormat),
            lastActivityDate: student.latestActivity
                ? moment(student.latestActivity.updatedAt).format(userDateTimeFormat)
                : "",
            lastActivity: student.latestActivity?.name,
            admissionAdvisorsHtml: renderList(student.admissionAdvisors, "fullName"),
            finAidAdvisorsHtml: renderList(student.finAidAdvisors, "fullName"),
            campusHtml: renderList(student.campuses),
            admChecklistStatusHtml: student.admChecklistStatus,
            faChecklistStatusHtml: student.faChecklistStatus,
            applicationFeeHtml: renderApplicationFeeHtml(student.applicationFee),
            preferredTimeOfContact: student.preferredTimeOfContact,
            studentStatusHtml: student.status ? `${student.status.name} [${student.status.state}]` : null
        }
    }

    const getColumns = useCallback(() => {
        const isFinancialTab = isFinancialPackagingTab || isFinancialVerificationTab

        const columns = [
            isFinancialTab
                ? null
                : {
                      title: "Activity",
                      field: "tickHtml",
                      isHideTitle: true,
                      style: {width: 50, padding: "3px 4px", textAlign: "right", lineHeight: "1.2"},
                      headerStyle: {width: 50, padding: "3px 4px", lineHeight: "1.2"},
                      columnIndex: 1,
                      render: (_, record) => renderTickHtml(record)
                  },
            isFinancialTab
                ? null
                : {
                      title: "Message",
                      field: "chatHtml",
                      isHideTitle: true,
                      style: {width: 50, padding: "3px 4px", textAlign: "right", lineHeight: "1.2"},
                      headerStyle: {width: 50, padding: "3px 4px", lineHeight: "1.2"},
                      columnIndex: 2,
                      render: (_, record) => renderChatHtml(record)
                  },
            {
                title: "Date Added",
                field: "createdAt",
                headerStyle: {minWidth: "90px", padding: "0px 5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "0px 5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 3,
                sortable: true
            },
            {
                title: t("studentInfo.enrollmentID"),
                field: "customProfileId",
                headerStyle: {minWidth: "150px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 14,
                sortable: true
            },
            {
                title: t("studentInfo.userState"),
                field: "profileState",
                headerStyle: {minWidth: "150px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 15,
                sortable: true
            },
            {
                title: t("studentInfo.userID"),
                field: "customUserId",
                headerStyle: {minWidth: "80px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 4,
                sortable: true
            },
            {
                title: t("studentInfo.firstName"),
                field: "firstName",
                orderField: "name",
                headerStyle: {minWidth: "100px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 5,
                sortable: true
            },
            {
                title: t("studentInfo.lastName"),
                field: "lastName",
                headerStyle: {minWidth: "100px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 6,
                sortable: true
            },
            {
                title: t("studentInfo.preferredName"),
                field: "nickNamePreferedName",
                headerStyle: {minWidth: "100px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 60,
                sortable: true
            },
            !isFinancialTab
                ? null
                : {
                      title: t("studentInfo.isirCode"),
                      field: "isirCode",
                      columnIndex: 61
                  },
            !isFinancialTab
                ? null
                : {
                      title: t("studentInfo.isirDate"),
                      field: "isirDate",
                      columnIndex: 62,
                      render: (date?: string) => (date ? moment(date).format(userDateFormat) : "")
                  },
            !isFinancialTab
                ? null
                : {
                      title: t("studentInfo.isirSAI"),
                      field: "isirSAI",
                      columnIndex: 63
                  },
            !isFinancialVerificationTab
                ? null
                : {
                      title: t("studentInfo.verificationStatus"),
                      field: "verificationStatusId",
                      columnIndex: 64,
                      render: (verificationStatusId?: number) => {
                          return (
                              verificationStatuses.find((status) => status.statusId === verificationStatusId)?.name ??
                              ""
                          )
                      }
                  },
            !isFinancialPackagingTab
                ? null
                : {
                      title: t("studentInfo.packagingStatus"),
                      field: "packagingStatus",
                      columnIndex: 65,
                      render: (status?: FinAidOfferStatus) =>
                          status ? t(`financialAid:finAidOffer.status.${status}`) : ""
                  },
            !isFinancialTab
                ? null
                : {
                      title: t("studentInfo.faStatusUpdatedAt"),
                      field: "faStatusUpdatedAt",
                      columnIndex: 66,
                      render: (date?: string) => (date ? moment(date).format(userDateFormat) : "")
                  },
            !isFinancialTab
                ? null
                : {
                      title: t("studentInfo.faStatusUpdatedBy"),
                      field: "faStatusUpdatedByProfile",
                      columnIndex: 67,
                      render: (profile) => getFullName(profile)
                  },
            {
                title: t("studentInfo.programInterest"),
                field: "programName",
                headerStyle: {minWidth: "100px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {
                    maxWidth: "180px",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    padding: "0px 5px",
                    margin: "0px",
                    lineHeight: "1.2"
                },
                columnIndex: 7,
                sortable: true
            },
            {
                title: t("studentInfo.phone"),
                field: "phone",
                headerStyle: {minWidth: "100px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 8,
                sortable: true
            },
            {
                title: t("studentInfo.studentStatus"),
                field: "studentStatusHtml",
                orderField: "statusId",
                headerStyle: {minWidth: "100px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 55,
                sortable: true
            },
            {
                title: t("studentInfo.isAccessDisabled"),
                field: "isAccessDisabled",
                columnIndex: 63,
                sortable: true,
                render: (isAccessDisabled: boolean) =>
                    isAccessDisabled ? (
                        <Icon icon="INACTIVE" color="var(--secondary-400-base)" />
                    ) : (
                        <Icon icon="ACTIVE" />
                    )
            },
            {
                title: "Last Activity Date",
                field: "lastActivityDate",
                orderField: "latestActivityDate",
                headerStyle: {minWidth: "90px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 10,
                sortable: true
            },
            {
                title: "Last Activity",
                field: "lastActivity",
                orderField: "latestActivityDate",
                headerStyle: {minWidth: "100px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 11,
                sortable: true
            },
            {
                title: t("studentInfo.email"),
                field: "email",
                headerStyle: {minWidth: "140px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 12,
                sortable: true
            },
            isFinancialTab
                ? null
                : {
                      title: t("studentInfo.leadSource"),
                      field: "admissionsLeadSourceId",
                      headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                      columnIndex: 21,
                      style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                      orderField: "leadSource",
                      sortable: true,
                      render: (admissionsLeadSourceId: number | null, record: Auth.DepartmentStudent) => {
                          const selectedLeadSource = (leadSources || []).find((v) => v.id === admissionsLeadSourceId)
                          return (
                              <div onClick={(e) => e.stopPropagation()}>
                                  <KlassDropdown
                                      isClearable
                                      options={leadSources}
                                      value={selectedLeadSource}
                                      getOptionLabel={(option: any) => [option.code, option.name].join(" - ")}
                                      onChange={(option) => {
                                          props.dispatch({
                                              data: getCurrentData().map((student) =>
                                                  student.profileId !== record.profileId
                                                      ? student
                                                      : {...student, admissionsLeadSourceId: option?.id ?? null}
                                              )
                                          })
                                          updateProfileAdmissionLeadSource(record.profileId, option?.id ?? null)
                                      }}
                                      placeholder="Select"
                                      menuPortalTarget={document.body}
                                  />
                              </div>
                          )
                      },
                      renderText: (admissionsLeadSourceId: number | null, student: Auth.DepartmentStudent) => {
                          const selectedLeadSource = (leadSources || []).find((v) => v.id === admissionsLeadSourceId)
                          if (!selectedLeadSource) return ""
                          return [selectedLeadSource.code, selectedLeadSource.name].join(" - ")
                      }
                  },
            isFinancialTab
                ? null
                : {
                      title: t("studentInfo.leadType"),
                      field: "leadTypeId",
                      headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                      columnIndex: 61,
                      style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                      orderField: "leadTypeId",
                      sortable: true,
                      render: (leadTypeId: number | null, record: Auth.DepartmentStudent) => {
                          return (
                              <div onClick={(e) => e.stopPropagation()}>
                                  <KlassDropdown
                                      isClearable
                                      options={leadTypes}
                                      value={leadTypes.find((type) => type.leadTypeId === leadTypeId)}
                                      getOptionLabel={(option: any) => [option.code, option.name].join(" - ")}
                                      onChange={(option) => {
                                          props.dispatch({
                                              data: getCurrentData().map((student) =>
                                                  student.profileId !== record.profileId
                                                      ? student
                                                      : {...student, leadTypeId: option?.id ?? null}
                                              )
                                          })
                                          updateProfileLeadType(record.profileId, option?.id ?? null)
                                      }}
                                      placeholder="Select"
                                      menuPortalTarget={document.body}
                                  />
                              </div>
                          )
                      },
                      renderText: (leadTypeId: number | null, student: Auth.DepartmentStudent) => {
                          const leadType = leadTypes.find((type) => type.leadTypeId === leadTypeId)
                          if (!leadType) return ""
                          return [leadType.code, leadType.name].join(" - ")
                      }
                  },
            {
                title: t("studentInfo.campusInterest"),
                field: "campusHtml",
                orderField: "campus",
                headerStyle: {minWidth: "100px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {
                    maxWidth: "180px",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    padding: "0px 5px",
                    margin: "0px",
                    lineHeight: "1.2"
                },
                columnIndex: 13,
                sortable: true
            },
            {
                title: t("studentInfo.marketingId"),
                field: "marketingId",
                orderField: "marketingId",
                headerStyle: {minWidth: "100px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {
                    maxWidth: "330px",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    padding: "0px 5px",
                    margin: "0px",
                    lineHeight: "1.2"
                },
                columnIndex: 68
            },
            {
                title: t("studentInfo.initialInquiryDate"),
                field: "initialInquiryDate",
                fieldType: "dateUtc",
                format: userDateFormat,
                render: (value) => (value ? moment.utc(value).format(userDateFormat) : ""),
                headerStyle: {minWidth: "100px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 16,
                sortable: true
            },
            {
                title: t("studentInfo.applicationCompletedDate"),
                field: "applicationCompletedDate",
                fieldType: "dateUtc",
                format: userDateFormat,
                render: (value) => (value ? moment.utc(value).format(userDateFormat) : ""),
                headerStyle: {minWidth: "180px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 17,
                sortable: true
            },
            {
                title: t("studentInfo.acceptanceDate"),
                field: "acceptanceDate",
                fieldType: "dateUtc",
                format: userDateFormat,
                render: (value) => (value ? moment.utc(value).format(userDateFormat) : ""),
                headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 18,
                sortable: true
            },
            {
                title: t("studentInfo.gender"),
                field: "gender",
                headerStyle: {minWidth: "100px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 19,
                sortable: true
            },
            {
                title: t("studentInfo.applicationFee"),
                field: "applicationFeeHtml",
                headerStyle: {minWidth: "180px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                orderField: "applicationFee",
                columnIndex: 20,
                sortable: true
            },
            {
                title: t("studentInfo.birthDate"),
                field: "birthDate",
                fieldType: "date",
                format: userDateFormat,
                render: (value) => (value ? moment(value).format(userDateFormat) : ""),
                headerStyle: {minWidth: "150px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 22,
                sortable: true
            },
            {
                title: t("studentInfo.race"),
                field: "race",
                headerStyle: {minWidth: "150px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 23,
                sortable: true
            },
            {
                title: t("studentInfo.maritalStatus"),
                field: "maritalStatus",
                headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 24,
                sortable: true
            },
            {
                title: t("studentInfo.country"),
                field: "country",
                headerStyle: {minWidth: "150px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 25,
                sortable: true
            },
            {
                title: t("studentInfo.address"),
                field: "address",
                headerStyle: {minWidth: "150px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 26,
                sortable: true
            },
            {
                title: t("studentInfo.city"),
                field: "city",
                headerStyle: {minWidth: "150px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 27,
                sortable: true
            },
            {
                title: t("studentInfo.state"),
                field: "state",
                headerStyle: {minWidth: "150px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 28,
                sortable: true
            },
            {
                title: t("studentInfo.postalCode"),
                field: "postalCode",
                headerStyle: {minWidth: "180px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 29,
                sortable: true
            },
            {
                title: t("studentInfo.highSchoolOrGED"),
                field: "highSchoolType",
                headerStyle: {minWidth: "250px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 30,
                sortable: true
            },
            {
                title: "High School - Date Certificate",
                field: "diplomaDate",
                fieldType: "dateUtc",
                format: userDateFormat,
                headerStyle: {minWidth: "250px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 69
            },
            {
                title: t("studentInfo.preferredTimeOfContact"),
                field: "preferredTimeOfContact",
                headerStyle: {minWidth: "260px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 31,
                render: (value) => value?.join(", ")
            },
            {
                title: t("studentInfo.howDidYouHearAboutUs"),
                field: "howDidYouKnowAboutUs",
                headerStyle: {minWidth: "320px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 32,
                sortable: true
            },
            {
                title: t("studentInfo.enrollmentState"),
                field: "profileState",
                headerStyle: {minWidth: "230px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 33,
                sortable: true
            },
            isFinancialTab
                ? null
                : {
                      title: t("studentInfo.admissionAdvisor"),
                      field: "admissionAdvisorsHtml",
                      orderField: useNewFilter ? "admissionAdvisor" : "admisionAdvisor",
                      headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                      style: {
                          maxWidth: "180px",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          padding: "0px 5px",
                          margin: "0px",
                          lineHeight: "1.2"
                      },
                      columnIndex: 38,
                      sortable: true
                  },
            isFinancialTab
                ? null
                : {
                      title: t("studentInfo.finAidAdvisor"),
                      field: "finAidAdvisorsHtml",
                      orderField: "finAidAdvisor",
                      headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                      style: {
                          maxWidth: "180px",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          padding: "0px 5px",
                          margin: "0px",
                          lineHeight: "1.2"
                      },
                      columnIndex: 39,
                      sortable: true
                  },
            {
                title: t("studentInfo.programVersion"),
                field: "programVersionName",
                headerStyle: {minWidth: "230px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 40,
                sortable: true
            },
            {
                title: t("studentInfo.degreeLevel"),
                field: "degreeLevel",
                headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 41,
                sortable: true
            },
            {
                title: t("studentInfo.term"),
                field: "termName",
                headerStyle: {minWidth: "150px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 42,
                sortable: true
            },
            {
                title: t("studentInfo.startingTerm"),
                field: "startingTermName",
                headerStyle: {minWidth: "150px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 43,
                sortable: true
            },
            {
                title: t("studentInfo.startDate"),
                field: "startDate",
                fieldType: "dateUtc",
                format: userDateFormat,
                render: (value) => (value ? moment(toDateOnly(value)).format(userDateFormat) : ""),
                headerStyle: {minWidth: "160px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 44,
                sortable: true
            },
            {
                title: t("studentInfo.contractEndDate"),
                field: "contractEndDate",
                fieldType: "dateUtc",
                format: userDateFormat,
                render: (value) => (value ? moment(toDateOnly(value)).format(userDateFormat) : ""),
                headerStyle: {minWidth: "300px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 45,
                sortable: true
            },
            {
                title: t("studentInfo.actualContractEndDate"),
                field: "actualContractEndDate",
                fieldType: "dateUtc",
                format: userDateFormat,
                render: (value) => (value ? moment(toDateOnly(value)).format(userDateFormat) : ""),
                headerStyle: {minWidth: "350px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 46,
                sortable: true
            },
            isFinancialTab
                ? null
                : {
                      title: t("studentInfo.academicTrack"),
                      field: "academicTrack",
                      headerStyle: {minWidth: "220px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                      style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                      columnIndex: 47,
                      sortable: true
                  },
            isFinancialTab
                ? null
                : {
                      title: t("studentInfo.admissionChecklist"),
                      field: "admChecklistStatusHtml",
                      headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                      columnIndex: 48,
                      style: {padding: "5px", margin: "0px", lineHeight: "1.2"}
                  },
            isFinancialTab
                ? null
                : {
                      title: t("studentInfo.finAidChecklist"),
                      field: "faChecklistStatusHtml",
                      headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                      columnIndex: 49,
                      style: {padding: "5px", margin: "0px", lineHeight: "1.2"}
                  },
            {
                title: "High School",
                field: "highSchoolName",
                headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 59,
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"}
            },
            showOnlyPartner
                ? {
                      title: "Partner",
                      field: "partnerName",
                      headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                      columnIndex: 56,
                      style: {padding: "5px", margin: "0px", lineHeight: "1.2"}
                  }
                : null,
            showOnlyPartner
                ? {
                      title: "Scholarship Date",
                      field: "scholarshipDate",
                      fieldType: "date",
                      format: userDateFormat,
                      render: (value) => (value ? moment(toDateOnly(value)).format(userDateFormat) : ""),
                      headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                      columnIndex: 57,
                      style: {padding: "5px", margin: "0px", lineHeight: "1.2"}
                  }
                : null,
            showOnlyPartner
                ? {
                      title: "Scholarship Amount",
                      field: "scholarshipAmount",
                      headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                      columnIndex: 58,
                      style: {padding: "5px", margin: "0px", lineHeight: "1.2"}
                  }
                : null,
            ...map(customFields?.data || [], (field) => ({
                title: field.label,
                field: "customFieldId",
                render: (_, record) => {
                    const customFieldValue = record.customFields?.find(
                        (item) => item.customFieldId === field.customFieldId
                    )
                    if (customFieldValue && customFieldValue.value) {
                        return field.fieldType === Settings.CustomFieldType.Date
                            ? moment.utc(customFieldValue.value).format(userDateFormat)
                            : customFieldValue.value || "-"
                    }
                    return "-"
                },
                headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: `cf_${field.customFieldId}`,
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"}
            }))
        ]
        const finAidColumns: any[] = [
            {
                title: "ISIR",
                field: "ISIR",
                fieldType: "date",
                format: userDateFormat,
                render: (value) => (value ? moment(toDateOnly(value)).format(userDateFormat) : ""),
                headerStyle: {minWidth: "150px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 50,
                sortable: false
            },
            {
                title: "EFC",
                field: "efc",
                headerStyle: {minWidth: "150px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 51,
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"}
            },
            {
                title: "Direct Loan MPN",
                field: "directLoanMPN",
                fieldType: "date",
                format: userDateFormat,
                render: (value) => (value ? moment(toDateOnly(value)).format(userDateFormat) : ""),
                headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 52,
                sortable: false
            },
            {
                title: "Entrance Counseling",
                field: "entranceCounseling",
                fieldType: "date",
                format: userDateFormat,
                render: (value) => (value ? moment(toDateOnly(value)).format(userDateFormat) : ""),
                headerStyle: {minWidth: "250px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 53,
                sortable: false
            },
            {
                title: t("studentInfo.FAPlanStatus"),
                field: "packageStatus",
                headerStyle: {minWidth: "200px", padding: "5px", margin: "0px", lineHeight: "1.2"},
                columnIndex: 54,
                style: {padding: "5px", margin: "0px", lineHeight: "1.2"}
            }
        ]
        return (props.departmentId === BaseDepartmentId.FinancialAid ? columns.concat(finAidColumns) : columns).filter(
            Boolean
        )
    }, [
        userDateFormat,
        renderTickHtml,
        renderChatHtml,
        props.departmentId,
        verificationStatuses,
        leadSources,
        leadTypes,
        updateProfileAdmissionLeadSource,
        updateProfileLeadType,
        isFinancialVerificationTab,
        isFinancialPackagingTab,
        customFields?.data
    ])

    const getFields = useCallback(() => {
        const isFinancialTab = isFinancialPackagingTab || isFinancialVerificationTab
        const columns = [
            isFinancialTab ? null : "Activity",
            isFinancialTab ? null : "Message",
            isFinancialTab ? null : "Date Added",
            t("studentInfo.enrollmentID"),
            isFinancialTab ? null : t("studentInfo.userState"),
            t("studentInfo.userID"),
            t("studentInfo.firstName"),
            t("studentInfo.lastName"),
            showOnlyHighschool ? "High School" : null,
            !isFinancialTab ? null : t("studentInfo.isirCode"),
            !isFinancialTab ? null : t("studentInfo.isirDate"),
            !isFinancialTab ? null : t("studentInfo.isirSAI"),
            !isFinancialVerificationTab ? null : t("studentInfo.verificationStatus"),
            !isFinancialPackagingTab ? null : t("studentInfo.packagingStatus"),
            !isFinancialTab ? null : t("studentInfo.term"),
            !isFinancialTab ? null : t("studentInfo.faStatusUpdatedAt"),
            !isFinancialTab ? null : t("studentInfo.faStatusUpdatedBy"),
            t("studentInfo.studentStatus"),
            isFinancialTab ? null : "Last Activity Date",
            isFinancialTab ? null : "Last Activity",
            isFinancialTab ? null : t("studentInfo.phone"),
            isFinancialTab ? null : t("studentInfo.email"),
            isFinancialTab ? null : t("studentInfo.leadSource"),
            isFinancialTab ? null : t("studentInfo.leadType"),
            isFinancialTab ? null : t("studentInfo.programInterest"),
            isFinancialTab ? null : t("studentInfo.campusInterest"),
            isFinancialTab ? null : t("studentInfo.marketingId"),
            t("studentInfo.customFields")
        ]
        if (showOnlyPartner) {
            columns.push("Partner", "Scholarship Date", "Scholarship Amount")
        }
        return columns.filter(Boolean)
    }, [isFinancialVerificationTab, isFinancialPackagingTab])

    useEffect(() => {
        if (isLoadedTableFuncs) {
            getStudents()
        }
    }, [
        isLoadedTableFuncs,
        page,
        pageSize,
        orderField?.field,
        orderField?.order,
        studentFormSearch,
        studentForm,
        fields,
        useNewFilter
    ])

    useEffect(() => {
        if (isFirstLoaded) {
            coreSocket.joinRoom(SOCKET_ROOM_PRIORITY_TABLE)
        }
        return () => {
            coreSocket.leaveRoom(SOCKET_ROOM_PRIORITY_TABLE)
        }
    }, [isFirstLoaded])

    useEffect(() => {
        const handleStudentDueChange = ({
            departmentId: dueDepartmentId,
            profileId,
            dueTime,
            dueColor
        }: {
            departmentId: number
            profileId: number
            dueTime?: string
            dueColor?: string
        }) => {
            if (departmentId !== dueDepartmentId) return
            props.dispatch({
                data: getCurrentData().map((student: Auth.DepartmentStudent) =>
                    student.profileId !== profileId
                        ? student
                        : {
                              ...student,
                              dueTime,
                              rowStyle: dueTime ? {background: dueColor || DUE_COLOR_OPTIONS[0].id} : undefined
                          }
                )
            })
        }

        const handleStudentStatusChange = async ({profileId, statusId}: {profileId: number; statusId: number}) => {
            if (!profileId) return

            const data = getCurrentData()
            const student = data.find((student: Auth.DepartmentStudent) => student.profileId === profileId)
            if (!student) return

            const newStatus = studentStatuses.find((status) => status.statusId === statusId)
            if (!newStatus) return

            props.dispatch({
                data: getCurrentData().map((student) =>
                    student.profileId !== profileId
                        ? student
                        : {
                              ...student,
                              profileState: newStatus.state,
                              studentStatusHtml: renderStudentStudentStatus(newStatus)
                          }
                )
            })
        }

        const handleStudentActivityCompletionToggled = async ({profileId}) => {
            try {
                const {
                    data: [newStudent]
                } = await studentService.getDepartmentStudents({
                    skipTotal: true,
                    filter: {departmentId, profileIds: [profileId], faTab}
                })
                props.dispatch({
                    data: getCurrentData().map((student, index) =>
                        student.profileId !== profileId ? student : getStudentRecord(newStudent, index)
                    )
                })
            } catch (error) {
                handleError(error)
            }
        }

        coreSocket.on(SocketEvent.ProfileDueSet, handleStudentDueChange)
        coreSocket.on(SocketEvent.ProfileDueCleared, handleStudentDueChange)
        coreSocket.on(SocketEvent.ProfileStudentStatusUpdated, handleStudentStatusChange)
        onEvent(EVENT_STUDENT_ACTIVITY_COMPLETION_TOGGLED, handleStudentActivityCompletionToggled)

        return () => {
            coreSocket.off(SocketEvent.ProfileDueSet, handleStudentDueChange)
            coreSocket.off(SocketEvent.ProfileDueCleared, handleStudentDueChange)
            coreSocket.off(SocketEvent.ProfileStudentStatusUpdated, handleStudentStatusChange)
            offEvent(EVENT_STUDENT_ACTIVITY_COMPLETION_TOGGLED, handleStudentActivityCompletionToggled)
        }
    }, [departmentId, getStudentRecord, faTab])

    useEffect(() => {
        function handleStudentActivityCreated(activity: StudentActivity) {
            props.dispatch({
                data: getCurrentData().map((student: Auth.DepartmentStudent) =>
                    student.profileId !== activity.profileId
                        ? student
                        : {
                              ...student,
                              unreadActivityIds: [...(student.unreadActivityIds ?? []), activity.studentActivityId],
                              dueTime: activity.completed ? undefined : student.dueTime,
                              rowStyle: activity.completed ? undefined : student.rowStyle
                          }
                )
            })
        }

        function handleStudentActivityRead({profileId}: {profileId: number}) {
            props.dispatch({
                data: getCurrentData().map((student: Auth.DepartmentStudent) =>
                    student.profileId !== profileId ? student : {...student, unreadActivityIds: []}
                )
            })
        }

        function handleStudentActivityUnread({profileId, activityIds}: {profileId: number; activityIds: number[]}) {
            props.dispatch({
                data: getCurrentData().map((student: Auth.DepartmentStudent) =>
                    student.profileId !== profileId ? student : {...student, unreadActivityIds: activityIds}
                )
            })
        }

        onEvent(EVENT_STUDENT_ACTIVITY_CREATED, handleStudentActivityCreated)
        onEvent(EVENT_STUDENT_ACTIVITY_READ, handleStudentActivityRead)
        onEvent(EVENT_STUDENT_ACTIVITY_UNREAD, handleStudentActivityUnread)
        return () => {
            offEvent(EVENT_STUDENT_ACTIVITY_CREATED, handleStudentActivityCreated)
            offEvent(EVENT_STUDENT_ACTIVITY_READ, handleStudentActivityRead)
            offEvent(EVENT_STUDENT_ACTIVITY_UNREAD, handleStudentActivityUnread)
        }
    }, [])

    useEffect(() => {
        props.dispatch({isClassComponent: false, isLoading: true})
        props.dispatchFunc([
            {key: "getListData", func: getStudents},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "getFilterMemoryKey", func: getFilterMemoryKey},
            {key: "getTableHeaderActions", func: getTableHeaderActions}
        ])
    }, [])

    useEffect(() => {
        props.dispatchFunc([{key: "getTableHeaderActions", func: getTableHeaderActions}])
    }, [taskData])

    useEffect(() => {
        props.dispatchFunc([{key: "getColumns", func: getColumns}])
    }, [getColumns])

    const getStudents = async () => {
        const loadingSeq = Math.random()
        loadingSeqRef.current = loadingSeq
        const storageData = model.getStorageFilter(filterMemoryKey)
        const page = storageData?.page || getCurrentPage()
        const pageSize = storageData?.pageSize || getCurrentPageSize()
        let orderFieldData = orderField
        if (storageData?.colOrd) {
            const columns: any[] = getColumns()
            const columnOrder = columns.find((column: any) => column.columnIndex === storageData.colOrd.idx)
            orderFieldData = {field: columnOrder?.orderField || columnOrder?.field, order: storageData.colOrd.order}
        }
        try {
            props.dispatch({isLoading: true})
            // activeStudentRef.current = undefined
            let _students: Auth.DepartmentStudent[] = [],
                _total: number = 0
            if (useNewFilter) {
                const {filters, departmentId, search} = getNewFilter()
                if (
                    !search &&
                    !Object.entries(filters).filter(
                        ([field, filter]) => !(field === "faTab" || (field === "includeArchive" && !filter?.value))
                    ).length
                ) {
                    _students = []
                    _total = 0
                } else {
                    const {data: students, total} = await studentServiceV2.getAll({
                        range: {pageSize, page},
                        sorts: [
                            {
                                orderBy: orderFieldData?.field ?? "createdAt",
                                orderDir: orderFieldData?.order ?? "desc"
                            }
                        ],
                        fields: [
                            ...fields,
                            "Dues",
                            "Inquiry Date",
                            "App comp date",
                            "Acceptance Date",
                            "Custom Fields"
                        ],
                        search,
                        departmentId,
                        filters
                    })
                    _students = students
                    _total = total
                }
            } else {
                const {data: students, total} = await studentService.getDepartmentStudents({
                    range: {pageSize, page},
                    sort: {
                        orderBy: orderFieldData?.field ?? "createdAt",
                        orderDir: orderFieldData?.order ?? "desc"
                    },
                    filter: getFilter()
                })
                _students = students
                _total = total
            }
            const studentsList = uniqBy(_students, "profileId").map(getStudentRecord)
            if (loadingSeq === loadingSeqRef.current) {
                props.dispatch({data: studentsList, total: _total})
                if (_total < (page - 1) * pageSize) {
                    props.onChangePage(1)
                }
            }
            setFirstLoaded(true)
            // DEFAULT: Hide student sidebar
            // if (!activeStudentRef.current && studentsList.length) {
            //     setTimeout(() => onChangeActiveStudent(studentsList[0].profileId), 200)
            // }
        } catch (error) {
            handleError(error)
        } finally {
            props.dispatch({isLoading: false})
        }
    }

    async function loadStudent(profileId: number, insertMissingRow = false) {
        try {
            const {
                data: [activeStudent]
            } = await studentService.getDepartmentStudents({
                skipTotal: true,
                filter: {departmentId: props.departmentId, profileIds: [profileId]}
            })
            if (activeStudentRef.current !== activeStudent?.profileId && !insertMissingRow) return
            props.updateState?.({activeStudent})
            const data = getCurrentData()
            const studentIndex = data.findIndex((student) => student.profileId === profileId)
            if (studentIndex === -1 && insertMissingRow) {
                props.dispatch({
                    data: [getStudentRecord(activeStudent, 0), ...data]
                })
                return
            }
            props.dispatch({
                data: getCurrentData().map((student, index) =>
                    student.profileId !== profileId ? student : getStudentRecord(activeStudent, index)
                )
            })
        } catch (error) {
            handleError(error)
        }
    }

    function onChangeActiveStudent(profileId: number) {
        let activeStudent
        const newData = getCurrentData().map((student: Auth.DepartmentStudent) => {
            const departmentDue = student.departmentDues?.find((due) => due.departmentId === departmentId)
            if (student.profileId === profileId) {
                activeStudent = student
            }
            return {
                ...student,
                isActiveTableCol: student.profileId === profileId,
                rowStyle:
                    student.profileId === profileId
                        ? undefined
                        : departmentDue
                        ? {background: departmentDue.dueColor || DUE_COLOR_OPTIONS[0].id}
                        : undefined
            }
        })
        activeStudentRef.current = profileId
        props.dispatch({data: newData})
        props.updateState?.({activeStudent})
    }

    const handleRowClick = React.useCallback(
        (item: Auth.DepartmentStudent) => onChangeActiveStudent(item.profileId),
        [onChangeActiveStudent]
    )

    const getFilterMemoryKey = () => {
        return filterMemoryKey
    }

    const getTableHeaderActions = () => {
        const checkedData = getCurrentData().filter((item) => item.isChecked)
        const checkedProfileStates = checkedData.map((item) => item.profileState)
        const actions = hasPermissionToUpdateAdvisor
            ? [
                  {
                      title: t("studentInfo.addAdvisor"),
                      icon: null,
                      action: () => onClickShowAdvisorModal("asign")
                  },
                  {
                      title: t("studentInfo.reAssignAdvisor"),
                      icon: null,
                      action: () => onClickShowAdvisorModal("reassign")
                  }
              ]
            : []
        if (
            DepartmentDialerModuleMapping[departmentId] &&
            isModuleEnable(DepartmentDialerModuleMapping[departmentId])
        ) {
            actions.push({
                title: "Add to Dialer",
                icon: null,
                action: addToDialer
            })
        }
        if (
            !isEmpty(checkedProfileStates) &&
            uniq(checkedProfileStates).length === 1 &&
            [
                Auth.StudentState.Prospect,
                Auth.StudentState.Applicant,
                Auth.StudentState.Enrollment,
                Auth.StudentState.Student
            ].includes(head(checkedProfileStates))
        ) {
            actions.push({
                title: "Change Status",
                icon: null,
                action: () => {
                    setStatusStudents(checkedData)
                    changeStatusPopup.open()
                }
            })
        }
        if (taskData.id && model.isStaff()) {
            actions.push({
                title: "Task",
                icon: null,
                action: () => {
                    setStudentProfileIds(checkedData.map(({profileId}) => profileId))
                    taskPopup.open()
                }
            })
        }
        if (checkedData.length) {
            actions.push({
                title: t("common:statsFilter.specialEvent"),
                icon: null,
                action: () => {
                    setStudentProfileIds(checkedData.map(({profileId}) => profileId))
                    specialEventPopup.open()
                }
            })
        }
        return actions
    }

    const getTask = async () => {
        try {
            const params = {
                filter: {
                    search: "",
                    subtype: TaskSubtype.LIBRARY,
                    departmentIds: [BaseDepartmentId.Academics],
                    formCode: TOTAL_HOURS_COMPLETED_REPORT_FORM_CODE
                },
                range: {page: 1, pageSize: 100},
                sort: {orderBy: "createdAt", orderDir: "DESC"}
            }
            const {data} = await taskService.getAll(params)
            if (data.length) {
                const [taskInfo] = data
                const taskDetail = await taskService.getOne(taskInfo.id)
                setTaskData({...taskDetail})
            }
        } catch (e) {
            handleError(e)
        }
    }

    const addToDialer = async () => {
        const data = getCurrentData()
        const checkedData: any[] = data.filter((item) => item.isChecked)
        const profileIds = checkedData.map(({profileId}) => profileId)
        try {
            await autoDialerService.addToDialer({
                departmentId,
                studentProfileIds: profileIds
            })
            toastSuccess("Added to dialer")
            props.dispatch({
                data: data.map((student) => ({...student, isChecked: false}))
            })
            props.updateTableHeaderActions()
        } catch (error) {
            handleError(error)
        }
    }

    const onClickShowAdvisorModal = (action) => {
        const data = getCurrentData()
        const checkedData: any[] = data.filter((item) => item.isChecked)
        setStudentProfileIds(checkedData.map(({profileId}) => profileId))
        const campusIds = checkedData.map(({campusIds}) => campusIds)
        setStudentCampusIds(campusIds.flat())
        setType(action)
        advisorsPopup.open()
    }

    const onClosePopup = (reload: boolean) => {
        setType("")
        if (reload) {
            reloadStudentAdvisors()
            const data = getCurrentData()
            props.dispatch({
                data: data.map((student) => ({...student, isChecked: false}))
            })
            props.updateTableHeaderActions()
        }
        advisorsPopup.close()
    }

    const handleExportFileTypeSelect = async (type: ExportFileType) => {
        const reportInfo = useNewFilter
            ? MODULE_REPORT_INFO_NEW_FILTERS[departmentId]
            : MODULE_REPORT_INFO[departmentId]
        if (total > 1000) {
            try {
                props.updateState?.({isExporting: true})
                const reportFilter = useNewFilter ? getNewFilter() : getFilter()
                const reportColumns = getColumns()
                    .filter((col) => fields.includes(col.title) && !["Activity", "Message"].includes(col.title))
                    .map((column: any) => ({field: column.field, title: column.title, fieldType: column.fieldType}))
                await reportsService.generateReport({
                    reportKey: reportInfo.reportKey,
                    payload: {filter: reportFilter, fields: reportColumns},
                    format: type
                })
                toastInfo(
                    "The report contains more than 1000 records; it will take some time to generate it. We will send you a notification with the download link when the report is ready"
                )
            } catch (error) {
                handleError(error)
            } finally {
                props.updateState?.({isExporting: false, exportFileType: null})
            }
            return
        }
        try {
            props.updateState?.({isExporting: true})
            const storageData = model.getStorageFilter(filterMemoryKey)
            const page = 1
            const pageSize = UNLIMITED_PAGE_SIZE
            let orderFieldData = orderField
            if (storageData?.colOrd) {
                const columns: any[] = getColumns()
                const columnOrder = columns.find((column: any) => column.columnIndex === storageData.colOrd.idx)
                orderFieldData = {field: columnOrder?.orderField || columnOrder?.field, order: storageData.colOrd.order}
            }
            let allStudents: Auth.DepartmentStudent[] = []
            if (useNewFilter) {
                const {filters, departmentId, search} = getNewFilter()
                const {data: _allStudents} = await studentServiceV2.getAll({
                    range: {pageSize, page},
                    sorts: [
                        {
                            orderBy: orderFieldData?.field ?? "createdAt",
                            orderDir: orderFieldData?.order ?? "desc"
                        }
                    ],
                    fields: [...fields, "Dues"],
                    search,
                    departmentId,
                    filters
                })
                allStudents = _allStudents
            } else {
                const filterParams = getFilter()
                const {data: _allStudents} = await studentService.getDepartmentStudents({
                    range: {pageSize, page},
                    sort: {
                        orderBy: orderFieldData?.field ?? "createdAt",
                        orderDir: orderFieldData?.order ?? "desc"
                    },
                    filter: filterParams
                })
                allStudents = _allStudents
            }
            const reportData = uniqBy(allStudents, "profileId").map(getStudentRecordByReport)

            const reportColumns = getColumns().filter(
                (col) => fields.includes(col.title) && !["Activity", "Message"].includes(col.title)
            )
            const filename = reportInfo.filename

            const payload = {
                filename,
                columns: reportColumns.map((col) => col.title),
                rows: reportData.map((item) =>
                    reportColumns.map((col: any) => {
                        const value = col.renderText
                            ? col.renderText(get(item, col.field), item)
                            : col.render
                            ? col.render(get(item, col.field), item)?.toString()
                            : get(item, col.field)?.toString()
                        return (type === "csv" ? `"${value?.replaceAll(/[|,;"]/g, "") ?? ""}"` : value) || ""
                    })
                )
            }
            if (type === "csv") {
                exportCsv(payload)
            } else if (type === "excel") {
                exportExcel(payload)
            }
        } catch (error) {
            handleError(error)
        } finally {
            props.updateState?.({isExporting: false, exportFileType: null})
        }
    }

    const isEmptyFilter = useMemo(() => {
        if (hightlightedStudentId) return false
        if (!useNewFilter) return false
        const {filters, departmentId, search} = getNewFilter()
        return (
            !search &&
            !Object.entries(filters).filter(
                ([field, filter]) => !(field === "faTab" || (field === "includeArchive" && !filter?.value))
            ).length
        )
    }, [useNewFilter, getNewFilter, hightlightedStudentId])

    const handleActivityUpserted = useCallback(
        (activity: StudentActivity) => {
            if (selectedStudent?.profileId) {
                if (activeStudentRef.current === selectedStudent.profileId) {
                    onChangeActiveStudent(selectedStudent?.profileId)
                }
            }
        },
        [selectedStudent?.profileId, onChangeActiveStudent]
    )

    return (
        <div className={styles.root}>
            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                isShowCheckedColumn
                columns={columns}
                data={data}
                isLoading={isLoading}
                fields={fields}
                allFields={allFields}
                orderField={orderField}
                onClickRowItem={handleRowClick}
                onChangeFields={props.onChangeFields}
                onUpdateRowData={props.onUpdateRowData}
                onUpdateTableData={props.onUpdateTableData}
                onClickSortColumn={props.onClickSortColumn}
                onDraggableColumn={props.onDraggableColumn}
                onChangeAllFields={props.onChangeAllFields}
                tableClassName={styles.studentTable}
                emptyText={isEmptyFilter ? t("common:table.emptyFilter") : t("common:table.emptyData")}
            />

            {!!activityPopup.isVisible && (
                <ActivityPopup
                    isShow={activityPopup.isVisible}
                    onClose={activityPopup.close}
                    studentId={selectedStudent?.profileId}
                    departmentId={departmentId}
                    isCompactForm={false}
                    setReload={handleActivityUpserted}
                />
            )}

            {!!chatPopup.isVisible && (
                <ChatModalV2
                    user={selectedStudent}
                    onShow={chatPopup.isVisible}
                    onClose={chatPopup.close}
                    userIdField="userId"
                    departmentId={departmentId}
                />
            )}
            {!!advisorsPopup.isVisible && (
                <AdvisorsPopup
                    isVisible={advisorsPopup.isVisible}
                    close={onClosePopup}
                    profileIds={studentProfileIds}
                    campusIds={studentCampusIds}
                    departmentId={departmentId}
                    advisorTitle={props.advisorTitle}
                    addMultiple
                    type={type}
                />
            )}
            <ChangeStatusPopup
                isShow={changeStatusPopup.isVisible}
                students={statusStudents}
                onClose={changeStatusPopup.close}
                onReload={() => {
                    props.dispatch({isShowTableHeaderAction: false, tableHeaderActions: []})
                    getStudents()
                }}
            />
            <PopupSpecialEvent
                isShow={specialEventPopup.isVisible}
                studentProfileIds={studentProfileIds}
                onClose={specialEventPopup.close}
                onSuccess={() => {
                    specialEventPopup.close()
                    props.dispatch({isShowTableHeaderAction: false, tableHeaderActions: []})
                    getStudents()
                }}
            />
            <PopupCreateTask
                isShow={taskPopup.isVisible}
                onClose={taskPopup.close}
                studentProfileIds={studentProfileIds}
                onSuccess={() => {
                    props.dispatch({isShowTableHeaderAction: false, tableHeaderActions: []})
                    getStudents()
                    taskPopup.close()
                }}
                taskData={taskData}
            />
        </div>
    )
})

export default KlassappTableHOC(StudentListTable)
